import { type MemberType } from "#/Actions/General/PresenceActions.ts";
import type SocketAgent from "./SocketAgent";
import { SOCKET_AGENT_EVENTS, SOCKET_WORKER_EVENTS } from "./SocketEvents";

export type SubchannelCallback = (message: any) => void;

export class SubchannelConnection {
    protected listeners: Record<string, Record<string, Set<SubchannelCallback>>> = {};
    protected members: Array<MemberType> = [];
    protected instances: Set<string> = new Set<string>();

    constructor(
        protected socketAgent: SocketAgent,
        protected name: string,
        protected id?: string
    ) {}

    public addListener(id: string, type: string, callback: SubchannelCallback): void {
        if(!this.listeners[type]) {
            this.listeners[type] = {
                [id]: new Set<SubchannelCallback>(),
            };
        } else if(!this.listeners[type][id]) {
            this.listeners[type][id] = new Set<SubchannelCallback>();
        }

        this.listeners[type][id].add(callback);
    }

    public removeListener(id: string, type: string, callback: SubchannelCallback): void {
        this.listeners[type]?.[id]?.delete(callback);
    }

    public handleMessage(data: {
        type: SOCKET_WORKER_EVENTS;
        message: any;
    }): void {
        if(data.type === SOCKET_WORKER_EVENTS.SUBCHANNEL_OVERRIDE) {
            this.members = data.message.members.map((member: MemberType) => ({
                connectionId: member.connectionId,
                clientId    : member.clientId,
            }));

            this.dispatchEvent("members", this.members);
        }

        if(data.type === SOCKET_WORKER_EVENTS.SUBCHANNEL_ENTER) {
            this.members.push({
                connectionId: data.message.member.connectionId,
                clientId    : data.message.member.clientId,
            });

            this.dispatchEvent("members", this.members);
        }

        if(data.type === SOCKET_WORKER_EVENTS.SUBCHANNEL_LEAVE) {
            const index = this.members.findIndex((member) => {
                return member.connectionId === data.message.member.connectionId;
            });

            if(index !== -1) {
                this.members.splice(index, 1);
            }

            this.dispatchEvent("members", this.members);
        }

        if(data.type === SOCKET_WORKER_EVENTS.SUBCHANNEL_MESSAGE) {
            this.dispatchEvent("message", data.message);
        }
    }

    protected dispatchEvent(type: string, message: any): void {
        if(!this.listeners[type]) {
            return;
        }

        for(const id of this.instances) {
            for(const callback of this.listeners[type][id]) {
                callback(message);
            }
        }
    }

    public updatePresence(): void {
        this.socketAgent.sendMessage(SOCKET_AGENT_EVENTS.PRESENCE_REQUEST, {
            name: this.name,
        });
    }

    public sendMessage(event: string, message: any): void {
        this.socketAgent.sendMessage(SOCKET_AGENT_EVENTS.SUBCHANNEL_MESSAGE, {
            name: this.name,
            id  : this.id,
            event,
            message,
        });
    }

    public connect(id: string): void {
        this.socketAgent.sendMessage(SOCKET_AGENT_EVENTS.SUBCHANNEL_CONNECT, {
            name: this.name,
            id  : this.id,
        });

        this.instances.add(id);
    }

    public disconnect(id: string): void {
        this.instances.delete(id);

        // Clear all the listeners
        for(const type in this.listeners) {
            if(!Object.hasOwn(this.listeners, type)) {
                continue;
            }

            this.listeners[type][id]?.clear();

            delete this.listeners[type][id];
        }

        if(this.instances.size === 0) {
            this.socketAgent.removeSubchannel(this.name);
        }
    }
}
